// Shown on the public /pricing page while public pricing is hidden (see config/pricing-visibility.ts).
// A single "Contact sales" panel replaces the tier price cards so no figure leaks, while the URL, the
// feature detail and the FAQ stay live so the page keeps its search surface and prospects still get a
// clear way to reach the team.
import { Link } from 'react-router-dom'

// The plan detail, minus the money. Kept verbatim from the tier cards so the page still answers
// "what do I actually get?" once the figures are gone.
const INCLUDED: { name: string; who: string; features: string[] }[] = [
  {
    name: 'Free',
    who: 'Try it on a few lanes before you subscribe.',
    features: [
      'Compare top carriers on demo lanes',
      'MCP demo key (smcp_demo_public)',
      'On-time % + transit on sample routes',
      'Community support',
    ],
  },
  {
    name: 'Starter',
    who: 'For shippers & small forwarders booking regularly.',
    features: [
      'Every lane, every carrier: full coverage',
      'Real on-time % + transit p50/p90 + sailing frequency',
      'Your own API key + MCP key (Claude, Cursor, …)',
      '90-day reliability history',
      'Email support · 1 seat',
    ],
  },
  {
    name: 'Business',
    who: 'For teams routing serious volume across many lanes.',
    features: [
      'Everything in Starter',
      'Carrier league tables across all your lanes',
      'Reliability-drop alerts on lanes you watch',
      '12-month history · higher query volume',
      'Multiple seats · priority support',
    ],
  },
]

export function ContactSalesPricing() {
  return (
    <>
      <div className="cp-card mx-auto max-w-3xl rounded-2xl border border-signal bg-surface-0 p-8 text-center shadow-[0_0_0_1px_var(--signal)]">
        <h2 className="font-display text-3xl" style={{ fontWeight: 210, letterSpacing: '-0.03em' }}>
          Pricing that fits your volume
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-[var(--text-secondary)] leading-relaxed">
          Tell us which lanes you book and how you want to query them, from the web app, the REST
          API, or MCP inside your own tools, and we will put a plan against it. Lane search on this
          site stays free, with no signup.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a
            href="mailto:sales@navo24.com?subject=SchedulesMCP%20pricing%20enquiry"
            className="rounded-xl bg-signal px-7 py-3 font-bold text-on-accent no-underline transition-opacity hover:opacity-90"
          >
            Contact sales
          </a>
          <Link
            to="/compare"
            className="rounded-xl border border-border px-7 py-3 font-bold text-[var(--text-primary)] no-underline transition-colors hover:border-signal/50"
          >
            Compare lanes free
          </Link>
        </div>
        <p className="mt-4 text-xs text-[var(--text-muted)]">
          Instant activation once you are set up. Cancel anytime. VAT invoice.
        </p>
      </div>

      <section className="mt-14">
        <h2 className="mb-8 text-center font-display font-light text-2xl tracking-[-0.015em]">
          What is included
        </h2>
        <div className="grid gap-6 md:grid-cols-3">
          {INCLUDED.map((t) => (
            <div
              key={t.name}
              className="cp-card flex flex-col rounded-2xl border border-border bg-surface-0 p-6"
            >
              <h3 className="font-display font-bold text-xl">{t.name}</h3>
              <p className="mt-2 mb-5 text-sm text-[var(--text-secondary)]">{t.who}</p>
              <ul className="space-y-2.5">
                {t.features.map((f) => (
                  <li key={f} className="flex gap-2 text-sm text-[var(--text-secondary)]">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                      role="presentation"
                      className="mt-0.5 shrink-0 text-signal"
                      style={{ width: '1em', height: '1em' }}
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </>
  )
}
